import React, { useEffect, useMemo, useState, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import styled, { keyframes } from 'styled-components';
import axios from 'axios';
import {
  Truck,
  Package as PackageIcon,
  RefreshCw,
  LogOut,
  MapPin,
  Clock,
  ArrowRight,
  Inbox,
  Send,
} from 'lucide-react';
import PackageScanner from '../../components/PackageScanner';
import ThirdPartyHandoffModal from '../../components/ThirdPartyHandoffModal/ThirdPartyHandoffModal';
import { useTranslation } from 'react-i18next';

const fadeIn = keyframes`
  from { opacity: 0; transform: translateY(8px); }
  to   { opacity: 1; transform: translateY(0); }
`;

const Page = styled.div`
  min-height: 100vh;
  background:
    radial-gradient(circle at 0% 0%, rgba(234, 88, 12, 0.10), transparent 45%),
    linear-gradient(180deg, #f8fafc 0%, #fff7ed 100%);
  padding: 1.5rem;
  display: flex;
  flex-direction: column;
  gap: 1.5rem;
`;

const Header = styled.header`
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 1rem;
  flex-wrap: wrap;
  animation: ${fadeIn} 0.3s ease;

  .brand {
    display: flex;
    align-items: center;
    gap: 0.875rem;
  }
  .icon-wrap {
    width: 48px;
    height: 48px;
    border-radius: 12px;
    background: linear-gradient(135deg, #ea580c 0%, #c2410c 100%);
    display: flex;
    align-items: center;
    justify-content: center;
    color: white;
    box-shadow: 0 6px 16px rgba(234, 88, 12, 0.3);
  }
  h1 {
    margin: 0;
    font-size: 1.4rem;
    font-weight: 700;
    color: #0f172a;
    line-height: 1.1;
  }
  p { margin: 0; font-size: 0.85rem; color: #64748b; }
`;

const Button = styled.button`
  display: inline-flex;
  align-items: center;
  gap: 0.4rem;
  padding: 0.55rem 0.95rem;
  border-radius: 10px;
  border: 1px solid #e2e8f0;
  background: white;
  color: #334155;
  font-size: 0.85rem;
  font-weight: 600;
  cursor: pointer;
  transition: background 0.15s ease;
  &:hover { background: #f1f5f9; }
  &.danger:hover { background: #fee2e2; color: #991b1b; border-color: #fecaca; }
`;

const Card = styled.section`
  background: white;
  border: 1px solid #e2e8f0;
  border-radius: 16px;
  padding: 1.25rem;
  box-shadow: 0 1px 2px rgba(15, 23, 42, 0.04);
  animation: ${fadeIn} 0.25s ease;
`;

const SectionTitle = styled.h2`
  margin: 0 0 0.875rem;
  font-size: 1rem;
  font-weight: 700;
  color: #0f172a;
  display: flex;
  align-items: center;
  gap: 0.5rem;
`;

const ColumnsGrid = styled.div`
  display: grid;
  grid-template-columns: repeat(auto-fit, minmax(320px, 1fr));
  gap: 1rem;
`;

const RoutePath = styled.div`
  display: flex;
  flex-wrap: wrap;
  align-items: center;
  gap: 0.65rem;
`;

const RouteStop = styled.span`
  display: inline-flex;
  align-items: center;
  gap: 0.35rem;
  padding: 0.45rem 0.65rem;
  border-radius: 999px;
  background: #fff7ed;
  border: 1px solid #fed7aa;
  color: #9a3412;
  font-weight: 800;
  font-size: 0.86rem;
`;

const RouteMeta = styled.div`
  display: flex;
  flex-wrap: wrap;
  gap: 0.75rem 1.25rem;
  color: #64748b;
  font-size: 0.9rem;
  margin-bottom: 0.9rem;

  span {
    display: inline-flex;
    align-items: center;
    gap: 0.35rem;
  }
`;

const RouteSchedule = styled.div`
  display: grid;
  gap: 0.9rem;
`;

const RouteScheduleItem = styled.div`
  border: 1px solid #fed7aa;
  border-radius: 14px;
  background: #fffbeb;
  padding: 0.9rem;
`;

const PackageCard = styled.div`
  background: white;
  border: 1px solid #e2e8f0;
  border-radius: 14px;
  padding: 0.875rem 1rem;
  display: flex;
  flex-direction: column;
  gap: 0.5rem;
  box-shadow: 0 1px 2px rgba(15, 23, 42, 0.04);

  .top {
    display: flex;
    align-items: center;
    justify-content: space-between;
    gap: 0.5rem;
  }
  .pkg-no {
    font-weight: 800;
    color: #0f172a;
    font-family: ui-monospace, monospace;
    letter-spacing: 0.02em;
  }
  .meta {
    display: flex;
    flex-wrap: wrap;
    gap: 0.5rem 1rem;
    color: #64748b;
    font-size: 0.8rem;
  }
  .row {
    display: flex;
    align-items: center;
    gap: 0.3rem;
  }
`;

const StatusPill = styled.span`
  display: inline-flex;
  align-items: center;
  gap: 0.25rem;
  padding: 0.125rem 0.55rem;
  border-radius: 999px;
  font-size: 0.72rem;
  font-weight: 700;
  background: ${(p) => p.bg || '#e0e7ef'};
  color: ${(p) => p.color || '#475569'};
`;

const STATUS_PILL = {
  packaged_at_store:    { labelKey: 'courier.statusAwaitingPickup',  bg: '#fef3c7', color: '#92400e' },
  in_transit_to_central:{ labelKey: 'courier.statusInTransitOut', bg: '#dbeafe', color: '#1e40af' },
  at_central:           { labelKey: 'courier.statusAtCentral',       bg: '#fce7f3', color: '#9d174d' },
  at_lab:               { labelKey: 'courier.statusAtLab',           bg: '#ede9fe', color: '#5b21b6' },
  at_central_returned:  { labelKey: 'courier.statusBackAtCentral',  bg: '#fce7f3', color: '#9d174d' },
  in_transit_to_store:  { labelKey: 'courier.statusInTransitBack', bg: '#dbeafe', color: '#1e40af' },
  delivered_to_store:   { labelKey: 'courier.statusDelivered',        bg: '#dcfce7', color: '#166534' },
  with_third_party:     { labelKey: 'courier.statusWithThirdParty',   bg: '#ffedd5', color: '#9a3412' },
  cancelled:            { labelKey: 'courier.statusCancelled',        bg: '#fee2e2', color: '#991b1b' },
};

const formatDateTime = (d) => {
  if (!d) return '';
  try { return new Date(d).toLocaleString(); } catch (_) { return ''; }
};

const CourierDashboard = () => {
  const { t } = useTranslation();
  const navigate = useNavigate();

  const user = useMemo(() => {
    try { return JSON.parse(localStorage.getItem('user')) || {}; }
    catch { return {}; }
  }, []);

  const [packages, setPackages] = useState([]);
  const [loading, setLoading] = useState(false);
  const [refreshKey, setRefreshKey] = useState(0);
  const [thirdPartyBusyId, setThirdPartyBusyId] = useState(null);
  const [handoffTarget, setHandoffTarget] = useState(null);
  const [courierRoutes, setCourierRoutes] = useState([]);
  const [centralStore, setCentralStore] = useState(null);
  const [stores, setStores] = useState([]);

  const fetchPackages = useCallback(async () => {
    setLoading(true);
    try {
      const token = localStorage.getItem('token');
      // The packages we care about are anything not yet delivered.
      // List, then filter client-side into "to pick up" vs "in transit".
      const resp = await axios.get('/api/packages?limit=100', {
        headers: { Authorization: `Bearer ${token}` },
      });
      setPackages(resp.data?.items || []);
    } catch (err) {
      console.error('Failed to load packages:', err);
    } finally {
      setLoading(false);
    }
  }, []);

  const fetchCourierRoute = useCallback(async () => {
    try {
      const token = localStorage.getItem('token');
      const resp = await axios.get('/api/stores/courier-route', {
        headers: { Authorization: `Bearer ${token}` },
      });
      setCourierRoutes(resp.data?.routes || (resp.data?.route ? [resp.data.route] : []));
      setCentralStore(resp.data?.centralStore || null);
      setStores(resp.data?.stores || []);
    } catch (err) {
      console.error('Failed to load courier route:', err);
    }
  }, []);

  useEffect(() => { fetchPackages(); }, [fetchPackages, refreshKey]);
  useEffect(() => { fetchCourierRoute(); }, [fetchCourierRoute, refreshKey]);

  // Bucket packages by what the courier needs to do with them.
  const buckets = useMemo(() => {
    const awaitingPickup = packages.filter((p) => p.status === 'packaged_at_store');
    const inTransitOut   = packages.filter((p) => p.status === 'in_transit_to_central');
    const awaitingReturn = packages.filter((p) => p.status === 'at_central_returned');
    const inTransitBack  = packages.filter((p) => p.status === 'in_transit_to_store');
    // Travelling with an outside carrier. Shown to every courier, not just the
    // one who handed it over, because the point is that whoever meets the 3rd
    // party next can scan it and take it on.
    const withThirdParty = packages.filter((p) => p.status === 'with_third_party');
    return { awaitingPickup, inTransitOut, awaitingReturn, inTransitBack, withThirdParty };
  }, [packages]);

  const handleLogout = () => {
    localStorage.removeItem('token');
    localStorage.removeItem('user');
    navigate('/login');
  };

  const handleScanSuccess = () => {
    // After a successful scan, refresh so the bucket counts shift.
    setRefreshKey((k) => k + 1);
  };

  // Passing a package to an outside carrier mid-route. The name is required:
  // once it leaves with someone outside the chain, that name is the only record
  // of who has it.
  const handThirdParty = async (recipientName) => {
    const pkg = handoffTarget;
    if (!pkg?._id || !recipientName) return;
    setThirdPartyBusyId(pkg._id);
    try {
      await axios.post(`/api/packages/${pkg._id}/third-party-courier`, { recipientName }, {
        headers: { Authorization: `Bearer ${localStorage.getItem('token')}` },
      });
      setHandoffTarget(null);
      setRefreshKey((k) => k + 1);
    } catch (err) {
      alert(err.response?.data?.message || t('courier.handOffFailed'));
    } finally {
      setThirdPartyBusyId(null);
    }
  };

  const storeById = useMemo(
    () => new Map(stores.map((store) => [String(store._id || store.id), store])),
    [stores]
  );

  const getRouteStops = (route) => {
    const selectedStores = (route?.storeIds || []).map((id) => storeById.get(String(id))).filter(Boolean);
    return route?.direction === 'central_to_stores'
      ? [centralStore, ...selectedStores].filter(Boolean)
      : [...selectedStores, centralStore].filter(Boolean);
  };

  const renderPackageCard = (p) => {
    const pill = STATUS_PILL[p.status] || { bg: '#e0e7ef', color: '#475569' };
    const statusLabel = pill.labelKey
      ? t(pill.labelKey)
      : String(p.status || '').replace(/_/g, ' ');
    // A courier can pass on anything they are actually holding — the long
    // distance case. Once it is already with a 3rd party there is nothing to
    // hand over; it is collected by scanning instead.
    const canHandOff = p.status === 'in_transit_to_central' || p.status === 'in_transit_to_store';
    const carrier = p.raw_data?.thirdPartyHandoff?.name || p.rawData?.thirdPartyHandoff?.name || null;
    const orderCount = p.orders?.length || 0;
    return (
      <PackageCard key={p._id}>
        <div className="top">
          <span className="pkg-no">{p.packageNumber}</span>
          <StatusPill bg={pill.bg} color={pill.color}><PackageIcon size={11} /> {statusLabel}</StatusPill>
        </div>
        <div className="meta">
          <span className="row"><MapPin size={13} /> {p.originStore?.name || '—'}</span>
          <span className="row"><PackageIcon size={13} /> {t('courier.orderCount', { count: orderCount })}</span>
          <span className="row"><Clock size={13} /> {formatDateTime(p.updatedAt || p.createdAt)}</span>
          {p.status === 'with_third_party' && carrier && (
            <span className="row"><Truck size={13} /> {t('courier.carriedBy')} {carrier}</span>
          )}
        </div>
        {canHandOff && (
          <div style={{ marginTop: '0.6rem' }}>
            <Button
              onClick={() => setHandoffTarget(p)}
              disabled={thirdPartyBusyId === p._id}
              style={{ background: '#fff7ed', borderColor: '#fdba74', color: '#9a3412' }}
            >
              <Truck size={14} /> {thirdPartyBusyId === p._id ? t('courier.handing') : t('courier.handTo3rdParty')}
            </Button>
          </div>
        )}
      </PackageCard>
    );
  };

  return (
    <Page>
      <Header>
        <div className="brand">
          <div className="icon-wrap"><Truck size={24} /></div>
          <div>
            <h1>{t('courier.courier')}</h1>
            <p>{user.firstName ? t('courier.welcomeName', { name: user.firstName }) : t('courier.pickupAndDelivery')}</p>
          </div>
        </div>
        <div style={{ display: 'flex', gap: '0.5rem' }}>
          <Button onClick={() => setRefreshKey((k) => k + 1)} disabled={loading}>
            <RefreshCw size={14} /> {loading ? t('courier.refreshing') : t('courier.refresh')}
          </Button>
          <Button className="danger" onClick={handleLogout}>
            <LogOut size={14} /> {t('courier.logout')}
          </Button>
        </div>
      </Header>

      <Card>
        <SectionTitle><Truck size={18} /> {t('courier.scanAPackage')}</SectionTitle>
        <PackageScanner
          helpText={t('courier.scanHelp')}
          onSuccess={handleScanSuccess}
        />
      </Card>

      <Card>
        <SectionTitle><MapPin size={18} /> {t('courier.todaySCourierSchedule')}</SectionTitle>
        {courierRoutes.length > 0 ? (
          <RouteSchedule>
            {courierRoutes.map((route, routeIndex) => {
              const routeStops = getRouteStops(route);
              return (
                <RouteScheduleItem key={route.id || `${route.departureTime}-${routeIndex}`}>
                  <RouteMeta>
                    <span><Clock size={15} /> {t('courier.startAt')} {route.departureTime || '08:00'}</span>
                    <span>
                      <Truck size={15} />
                      {route.direction === 'central_to_stores' ? t('courier.centralToStores') : t('courier.storesToCentral')}
                    </span>
                  </RouteMeta>
                  <RoutePath>
                    {routeStops.length === 0 ? (
                      <span style={{ color: '#94a3b8', fontStyle: 'italic' }}>{t('courier.noStoresSelectedForThis')}</span>
                    ) : routeStops.map((stop, index) => (
                      <React.Fragment key={`${stop._id || stop.id}-${index}`}>
                        {index > 0 && <ArrowRight size={16} color="#fb923c" />}
                        <RouteStop><MapPin size={14} /> {stop.name}</RouteStop>
                      </React.Fragment>
                    ))}
                  </RoutePath>
                  {route.notes && (
                    <div style={{ marginTop: '0.9rem', color: '#475569', lineHeight: 1.5 }}>
                      {route.notes}
                    </div>
                  )}
                </RouteScheduleItem>
              );
            })}
          </RouteSchedule>
        ) : (
          <div style={{ color: '#94a3b8', fontStyle: 'italic' }}>
            {t('courier.noRoutesConfiguredYetAsk')}
          </div>
        )}
      </Card>

      <ColumnsGrid>
        <Card>
          <SectionTitle><Inbox size={18} /> {t('courier.pickupAtStore')}{buckets.awaitingPickup.length})</SectionTitle>
          {buckets.awaitingPickup.length === 0 ? (
            <div style={{ color: '#94a3b8', fontStyle: 'italic' }}>{t('courier.nothingWaitingAtAnyStore')}</div>
          ) : buckets.awaitingPickup.map(renderPackageCard)}
        </Card>

        <Card>
          <SectionTitle><ArrowRight size={18} /> {t('courier.inTransitToCentral')}{buckets.inTransitOut.length})</SectionTitle>
          {buckets.inTransitOut.length === 0 ? (
            <div style={{ color: '#94a3b8', fontStyle: 'italic' }}>{t('courier.noneOnTheRoad')}</div>
          ) : buckets.inTransitOut.map(renderPackageCard)}
        </Card>

        <Card>
          <SectionTitle><Inbox size={18} /> {t('courier.pickupAtCentral')}{buckets.awaitingReturn.length})</SectionTitle>
          {buckets.awaitingReturn.length === 0 ? (
            <div style={{ color: '#94a3b8', fontStyle: 'italic' }}>{t('courier.noFinishedPackagesWaiting')}</div>
          ) : buckets.awaitingReturn.map(renderPackageCard)}
        </Card>

        <Card>
          <SectionTitle><Send size={18} /> {t('courier.inTransitToStore')}{buckets.inTransitBack.length})</SectionTitle>
          {buckets.inTransitBack.length === 0 ? (
            <div style={{ color: '#94a3b8', fontStyle: 'italic' }}>{t('courier.noneOnTheWayBack')}</div>
          ) : buckets.inTransitBack.map(renderPackageCard)}
        </Card>

        <Card>
          <SectionTitle><Truck size={18} /> {t('courier.with3rdParty')}{buckets.withThirdParty.length})</SectionTitle>
          {buckets.withThirdParty.length === 0 ? (
            <div style={{ color: '#94a3b8', fontStyle: 'italic' }}>{t('courier.nothingTravellingWithAnOutside')}</div>
          ) : (
            <>
              <div style={{ color: '#64748b', fontSize: '0.8rem', marginBottom: '0.6rem' }}>
                {t('courier.scanOneOfTheseTo')}
              </div>
              {buckets.withThirdParty.map(renderPackageCard)}
            </>
          )}
        </Card>
      </ColumnsGrid>

      <ThirdPartyHandoffModal
        open={Boolean(handoffTarget)}
        packageNumber={handoffTarget?.packageNumber}
        busy={thirdPartyBusyId === handoffTarget?._id}
        onCancel={() => setHandoffTarget(null)}
        onConfirm={handThirdParty}
      />
    </Page>
  );
};

export default CourierDashboard;
